interface RateLimitEntry {
  attempts: number;
  firstAttemptAt: number;
  lockedUntil: number | null;
}

const store = new Map<string, RateLimitEntry>();

const MAX_ATTEMPTS = 5;
const WINDOW_MS = 15 * 60 * 1000; // 15 minutes
const LOCKOUT_MS = 15 * 60 * 1000; // 15 minutes lockout

export interface RateLimitResult {
  allowed: boolean;
  attemptsLeft: number;
  lockedUntil: number | null;
  message?: string;
}

export function checkRateLimit(key: string): RateLimitResult {
  const now = Date.now();
  const entry = store.get(key);

  if (!entry) {
    return { allowed: true, attemptsLeft: MAX_ATTEMPTS - 1, lockedUntil: null };
  }

  // Check if currently locked out
  if (entry.lockedUntil && now < entry.lockedUntil) {
    const secondsLeft = Math.ceil((entry.lockedUntil - now) / 1000);
    return {
      allowed: false,
      attemptsLeft: 0,
      lockedUntil: entry.lockedUntil,
      message: `Too many failed attempts. Try again in ${Math.ceil(secondsLeft / 60)} minute(s).`,
    };
  }

  // Reset window if expired
  if (now - entry.firstAttemptAt > WINDOW_MS) {
    store.delete(key);
    return { allowed: true, attemptsLeft: MAX_ATTEMPTS - 1, lockedUntil: null };
  }

  const attemptsLeft = MAX_ATTEMPTS - entry.attempts - 1;
  return { allowed: true, attemptsLeft, lockedUntil: null };
}

export function recordFailedAttempt(key: string): RateLimitResult {
  const now = Date.now();
  const entry = store.get(key);

  if (!entry) {
    store.set(key, { attempts: 1, firstAttemptAt: now, lockedUntil: null });
    return { allowed: true, attemptsLeft: MAX_ATTEMPTS - 1, lockedUntil: null };
  }

  // Reset if window expired
  if (now - entry.firstAttemptAt > WINDOW_MS) {
    store.set(key, { attempts: 1, firstAttemptAt: now, lockedUntil: null });
    return { allowed: true, attemptsLeft: MAX_ATTEMPTS - 1, lockedUntil: null };
  }

  entry.attempts += 1;

  if (entry.attempts >= MAX_ATTEMPTS) {
    entry.lockedUntil = now + LOCKOUT_MS;
    store.set(key, entry);
    return {
      allowed: false,
      attemptsLeft: 0,
      lockedUntil: entry.lockedUntil,
      message: `Too many failed attempts. Try again in 15 minute(s).`,
    };
  }

  store.set(key, entry);
  const attemptsLeft = MAX_ATTEMPTS - entry.attempts;
  return { allowed: true, attemptsLeft, lockedUntil: null };
}

export function resetRateLimit(key: string): void {
  store.delete(key);
}
